import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import axios from '../../../utils/axios';
import { Button } from '@/components/ui/button';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null); // 'success', 'error', 'expired'

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setVerificationStatus('error');
      setIsLoading(false);
      toast.error('Verification token is missing');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await axios.get(`/auth/verify-email/${token}`);
        if (response.data.success) {
          setVerificationStatus('success');
          toast.success('Email verified successfully!');
        } else {
          setVerificationStatus('error');
          toast.error(response.data.message || 'Email verification failed');
        }
      } catch (error) {
        console.error('Email verification error:', error);
        if (error.response?.status === 400 && error.response?.data?.message?.includes('expired')) {
          setVerificationStatus('expired');
          toast.error('Verification link has expired');
        } else {
          setVerificationStatus('error');
          toast.error(error.response?.data?.message || 'Email verification failed');
        }
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [token]);

  const handleResendVerification = async () => {
    try {
      setIsLoading(true);
      await axios.post('/auth/resend-verification');
      toast.success('Verification email sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend verification email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/auth/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-primary mb-2">Verifying your email...</h2>
          <p className="text-gray-600">Please wait while we verify your email address.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 max-w-md w-full text-center">
        {verificationStatus === 'success' ? (
          <>
            <div className="mb-6 flex justify-center">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01l-3-3" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-primary mb-4">Email Verified Successfully!</h2>
            <p className="text-gray-600 mb-6">Your email address has been verified. You can now access all features of your account.</p>
            <Button onClick={handleGoToLogin} className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white py-3 text-lg font-medium">
              Continue to Login
            </Button>
          </>
        ) : verificationStatus === 'expired' ? (
          <>
            <div className="mb-6 flex justify-center">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9v3.75m0 3.75h.0075M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-primary mb-4">Verification Link Expired</h2>
            <p className="text-gray-600 mb-6">This verification link has expired. Please request a new verification email.</p>
            <Button 
              onClick={handleResendVerification} 
              className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white py-3 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Resend Verification Email'}
            </Button>
          </>
        ) : (
          <>
            <div className="mb-6 flex justify-center">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9v3.75m0 3.75h.0075M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-primary mb-4">Verification Failed</h2>
            <p className="text-gray-600 mb-6">We couldn't verify your email address. The link may be invalid or expired.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={handleResendVerification} 
                variant="secondary" 
                className="flex-1 py-3 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Resend Verification Email'}
              </Button>
              <Button 
                onClick={handleGoToLogin} 
                className="flex-1 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white py-3 text-lg font-medium"
              >
                Go to Login
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
